import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SortableTable from '../../components/SortableTable';
import { fetchMerchants } from '../../services/firestore';

const PLAN_PRICE = { free: 0, starter: 499, growth: 999, pro: 1999, business: 999 };

function initials(value = '') {
  return String(value || 'WB')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'WB';
}

function toDate(value) {
  if (!value?.toDate) return '-';
  return value.toDate().toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

export default function MerchantList() {
  const [plan, setPlan] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await fetchMerchants({ plan, status });
        if (alive) setRows(data.merchants);
      } catch (err) {
        if (alive) setError(err?.message || 'Failed to load merchants.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [plan, status]);

  const columns = useMemo(() => [
    {
      key: 'name',
      header: 'Business',
      accessor: (merchant) => merchant.businessName || merchant.id,
      render: (merchant) => (
        <div className="wb-merchant-cell">
          <span className="wb-avatar">{initials(merchant.businessName || merchant.name || merchant.id)}</span>
          <span>
            <span className="wb-cell-main">{merchant.businessName || merchant.name || '-'}</span>
            <span className="wb-cell-sub">{merchant.city || merchant.state || merchant.id}</span>
          </span>
        </div>
      ),
    },
    { key: 'phone', header: 'Phone', accessor: 'phone', render: (merchant) => merchant.phone || '-' },
    {
      key: 'plan',
      header: 'Plan',
      accessor: (merchant) => merchant.plan || 'free',
      render: (merchant) => (
        <span className={`wb-tag wb-tag--${String(merchant.plan || 'free').toLowerCase()}`}>
          {merchant.plan || 'free'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      accessor: (merchant) => merchant.planStatus || 'unknown',
      render: (merchant) => (
        <span className={`wb-tag wb-tag--${String(merchant.planStatus || 'unknown').toLowerCase()}`}>
          {merchant.planStatus || '-'}
        </span>
      ),
    },
    {
      key: 'trialEnd',
      header: 'Billing',
      accessor: (merchant) => merchant.trialEndDate,
      render: (merchant) => {
        const plan = String(merchant.plan || 'free').toLowerCase();
        if (merchant.planStatus === 'trial') return <><span className="wb-cell-main">Trial</span><span className="wb-cell-sub">Ends {toDate(merchant.trialEndDate)}</span></>;
        if (PLAN_PRICE[plan]) return <><span className="wb-cell-main">Rs {PLAN_PRICE[plan]}/mo</span><span className="wb-cell-sub">Renews {toDate(merchant.subscriptionEndDate || merchant.renewalDate)}</span></>;
        return <><span className="wb-cell-main">Free plan</span><span className="wb-cell-sub">No billing</span></>;
      },
    },
    {
      key: 'invoices',
      header: 'Invoices',
      accessor: (merchant) => merchant.billCountThisMonth || merchant.invoiceCount || 0,
      render: (merchant) => Number(merchant.billCountThisMonth || merchant.invoiceCount || 0).toLocaleString('en-IN'),
    },
    {
      key: 'activity',
      header: 'Last Activity',
      accessor: (merchant) => merchant.updatedAt || merchant.createdAt,
      render: (merchant) => (merchant.updatedAt?.toDate ? merchant.updatedAt.toDate().toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'),
    },
    {
      key: 'actions',
      header: '',
      accessor: 'id',
      sortable: false,
      render: (merchant) => <Link className="wb-inline-link" to={`/merchants/${merchant.id}`}>View</Link>,
    },
  ], []);

  return (
    <div className="wb-stack">
      <div className="wb-dashboard-hero">
        <div>
          <div className="wb-page-kicker">Merchants - {rows.length.toLocaleString('en-IN')} loaded</div>
          <h2>Merchant list</h2>
          <p>Search by business name, phone, or status. Table controls stay compact for repeated admin work.</p>
        </div>
      </div>

      <div className="wb-grid">
        <div className="wb-card wb-stat-card"><span>Total</span><strong>{rows.length.toLocaleString('en-IN')}</strong><small>Current result set</small></div>
        <div className="wb-card wb-stat-card"><span>Active</span><strong>{rows.filter((row) => row.planStatus === 'active').length.toLocaleString('en-IN')}</strong><small>Paid or free active</small></div>
        <div className="wb-card wb-stat-card"><span>Trial</span><strong>{rows.filter((row) => row.planStatus === 'trial').length.toLocaleString('en-IN')}</strong><small>Trial accounts</small></div>
        <div className="wb-card wb-stat-card"><span>Past due</span><strong>{rows.filter((row) => ['expired', 'past_due'].includes(row.planStatus)).length.toLocaleString('en-IN')}</strong><small>Needs attention</small></div>
      </div>

      <div className="wb-card">
        <div className="wb-card-head">
          <div>
            <div className="wb-page-kicker">All merchants</div>
            <h3 className="wb-section-title">Merchant list</h3>
          </div>
        </div>
      <div className="wb-toolbar-grid" style={{ marginBottom: 10 }}>
        <input placeholder="Search by name or phone" value={q} onChange={(e) => setQ(e.target.value)} />
        <select value={plan} onChange={(e) => setPlan(e.target.value)}>
          <option value="">All plans</option>
          <option value="free">free</option><option value="starter">starter</option><option value="growth">growth</option><option value="pro">pro</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All status</option>
          <option value="trial">trial</option><option value="active">active</option><option value="expired">expired</option><option value="cancelled">cancelled</option>
        </select>
      </div>

      {loading ? <div className="wb-skeleton wb-skeleton--table" /> : null}
      {error ? <p className="wb-error-text">{error}</p> : null}
      {!loading ? (
        <SortableTable
          columns={columns}
          rows={rows}
          filter={q}
          emptyText="No merchants found for the current filters."
          defaultSort={{ key: 'name', dir: 'asc' }}
        />
      ) : null}
      </div>
    </div>
  );
}
